import { useState } from "react";
import { CheckCircle, HandPalm, Plus } from "@phosphor-icons/react";
import api from "../../../lib/api";
import { btnGhost, btnPrimary, fmtDateTimeWIB, inp, todayWIB } from "../util";
import { call, useLoad } from "./common";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../../ui/alert-dialog";

function DoneConfirm({ t, onConfirm }) {
    return (
        <AlertDialog>
            <AlertDialogTrigger asChild><button type="button" className={btnGhost} data-testid={`task-done-${t.id}`}><CheckCircle size={14} /> Done</button></AlertDialogTrigger>
            <AlertDialogContent data-testid={`task-done-dialog-${t.id}`}>
                <AlertDialogHeader>
                    <AlertDialogTitle>Tandai selesai?</AlertDialogTitle>
                    <AlertDialogDescription>Pastikan customer ini benar-benar sudah ditindaklanjuti sebelum ditandai selesai. Tandai selesai sekarang?</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel data-testid={`task-done-cancel-${t.id}`}>Batal</AlertDialogCancel>
                    <AlertDialogAction onClick={onConfirm} data-testid={`task-done-confirm-${t.id}`}>Ya, Sudah Ditindaklanjuti</AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}

export const TYPE_LABEL = { PENDING_PAYMENT: "Pending Payment", PENDING_SHIPMENT: "Pending Shipment", CUSTOMER_HANDOVER: "Customer Handover", COMPLAINT: "Complaint", LAINNYA: "Lainnya" };
const PROG = { MENUNGGU_DITERIMA: ["Menunggu Diterima", "text-orange-600"], DALAM_PROSES: ["Diterima / Dalam Proses", "text-sky-700"], SELESAI: ["Selesai", "text-emerald-700"], BATAL: ["Batal", "text-neutral-500"] };

export function TaskStatus({ t }) {
    const [l, c] = PROG[t.progress] || [t.display_status, ""];
    return <span className={`text-xs font-semibold shrink-0 ${c}`} data-testid={`task-status-${t.id}`}>{l}{t.overdue && <span className="text-rose-700"> · Terlambat</span>}</span>;
}

function TaskRow({ t, me, recipients, reload }) {
    const [to, setTo] = useState("");
    const [note, setNote] = useState("");
    const post = async (path, body, ok) => { if (await call(() => api.post(`/reporting/v2/tasks/${t.id}/${path}`, body), ok)) reload(); };
    const waiting = t.progress === "MENUNGGU_DITERIMA";
    return (
        <li className="py-3 border-b border-mist/70 last:border-0 space-y-1.5" data-testid={`task-${t.id}`}>
            <div className="flex items-start justify-between gap-2">
                <div className="text-sm"><b>{t.customer}</b> <span className="text-neutral-700">— {t.task}</span></div>
                <TaskStatus t={t} />
            </div>
            <div className="text-xs text-neutral-500">{t.kind === "HANDOVER" ? `Handover dari ${t.from_user_name}` : t.kind === "PENDING" ? "Pending saya" : "Follow-up saya"}{t.type ? ` · ${TYPE_LABEL[t.type]}` : ""} · s/d {t.deadline || "—"} · {fmtDateTimeWIB(t.created_at)}</div>
            {waiting ? (
                <button type="button" className={btnPrimary} data-testid={`task-accept-${t.id}`} onClick={() => post("accept", undefined, "Tugas diterima")}><HandPalm size={14} /> Terima tugas</button>
            ) : (
                <div className="flex flex-wrap gap-2">
                    <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Catatan selesai (opsional)" className={`${inp} !w-56`} data-testid={`task-note-${t.id}`} />
                    <DoneConfirm t={t} onConfirm={() => post("done", { note }, "Tugas selesai")} />
                    <select value={to} onChange={(e) => setTo(e.target.value)} className={`${inp} !w-44`} data-testid={`task-reassign-select-${t.id}`}>
                        <option value="">Handover ulang ke…</option>
                        {recipients.filter((r) => r.id !== me.id).map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                    </select>
                    {to && <button type="button" className={btnGhost} data-testid={`task-reassign-${t.id}`} onClick={() => post("reassign", { assignee_id: to }, "Tugas di-handover ulang")}>Kirim</button>}
                </div>
            )}
        </li>
    );
}

function NewTask({ me, recipients, done }) {
    const blank = { customer: "", task: "", type: "PENDING_PAYMENT", deadline: "", assignee_id: me.id };
    const [f, setF] = useState(null);
    if (!f) return <button type="button" className={`${btnGhost} mt-2`} onClick={() => setF(blank)} data-testid="task-new-open"><Plus size={14} /> Tambah pending customer</button>;
    const s = (k) => (e) => setF({ ...f, [k]: e.target.value });
    const save = async () => { if (await call(() => api.post("/reporting/v2/tasks", { ...f, deadline: f.deadline || null }), "Pending dicatat")) { setF(null); done(); } };
    return (
        <div className="grid grid-cols-1 min-[380px]:grid-cols-2 gap-2 mt-3 border border-mist rounded-sm p-3" data-testid="task-new-form">
            <input value={f.customer} onChange={s("customer")} placeholder="Customer" className={inp} data-testid="task-new-customer" />
            <select value={f.type} onChange={s("type")} className={inp} data-testid="task-new-type">{Object.entries(TYPE_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
            <input value={f.task} onChange={s("task")} placeholder="Yang perlu dikerjakan" className={`${inp} min-[380px]:col-span-2`} data-testid="task-new-task" />
            <select value={f.assignee_id} onChange={s("assignee_id")} className={inp} data-testid="task-new-assignee">
                <option value={me.id}>Saya sendiri</option>
                {recipients.filter((r) => r.id !== me.id).map((r) => <option key={r.id} value={r.id}>Handover ke {r.name}</option>)}
            </select>
            <label className="text-[11px] text-neutral-500">Deadline (kosong = akhir hari kerja berikutnya)<input type="date" min={todayWIB()} value={f.deadline} onChange={s("deadline")} className={inp} data-testid="task-new-deadline" /></label>
            <div className="flex gap-2 min-[380px]:col-span-2">
                <button type="button" className={btnPrimary} onClick={save} data-testid="task-new-save">Simpan</button>
                <button type="button" className={btnGhost} onClick={() => setF(null)} data-testid="task-new-cancel">Batal</button>
            </div>
        </div>
    );
}

export default function Tasks({ me, onChanged }) {
    const [items, load] = useLoad("/reporting/v2/tasks", { scope: "mine", d: todayWIB() });
    const reload = () => { load(); onChanged?.(); };
    const [recipients] = useLoad("/reporting/v2/recipients");
    if (!items || !recipients) return null;
    const active = items.filter((t) => t.assignee_id === me.id && t.status === "PENDING");
    const since = new Date(Date.now() - 2 * 864e5).toISOString();
    const recent = items.filter((t) => t.status !== "PENDING" && (t.done_at || t.updated_at || t.created_at) >= since && (t.assignee_id === me.id || t.from_user_id === me.id)).slice(0, 8);
    const sent = items.filter((t) => t.status === "PENDING" && t.from_user_id === me.id && t.assignee_id !== me.id);
    const waiting = active.filter((t) => t.progress === "MENUNGGU_DITERIMA").length;
    return (
        <section className="bg-white border border-mist rounded-sm p-4" data-testid="tasks-incoming">
            <h3 className="text-base font-semibold">Tugas Masuk</h3>
            <p className={`text-sm ${active.length ? "text-orange-600 font-medium" : "text-neutral-500"}`} data-testid="tasks-count">{active.length ? `${active.length} belum selesai${waiting ? ` · ${waiting} menunggu diterima` : ""}` : "Tidak ada tugas aktif"}</p>
            <ul>{active.map((t) => <TaskRow key={t.id} t={t} me={me} recipients={recipients} reload={reload} />)}</ul>
            <NewTask me={me} recipients={recipients} done={reload} />
            {sent.length > 0 && (
                <div className="mt-3 text-xs text-neutral-600 space-y-1" data-testid="tasks-sent">
                    <div className="font-medium">Handover saya yang masih dikerjakan</div>
                    {sent.map((t) => <div key={t.id} className="flex justify-between gap-2" data-testid={`tasks-sent-${t.id}`}><span>{t.customer} → {t.assignee_name}{t.type ? ` · ${TYPE_LABEL[t.type]}` : ""} · s/d {t.deadline || "—"}</span><TaskStatus t={t} /></div>)}
                </div>
            )}
            {recent.length > 0 && (
                <details className="mt-3" data-testid="tasks-recent">
                    <summary className="text-xs text-neutral-600 cursor-pointer">Riwayat 2 hari terakhir ({recent.length})</summary>
                    {recent.map((t) => <div key={t.id} className="flex justify-between gap-2 text-xs py-1 border-b border-mist/60"><span>{t.customer} — {t.task} · {t.assignee_name}{t.done_note ? ` · ${t.done_note}` : ""}</span><TaskStatus t={t} /></div>)}
                    <p className="text-[11px] text-neutral-500 mt-1">Riwayat lebih lama ada di menu History (Report PIC).</p>
                </details>
            )}
        </section>
    );
}
